<script setup>
import {
	nextTick,
	onMounted,
	onUnmounted,
	onBeforeUnmount,
	ref,
	watchEffect,
} from "vue";
import { useRouter } from "vue-router";
import { useMainStore } from "@/stores/store.js";
import ModalValidation from "./StepProduct_ModalValidation.vue";
import ModalSelection from "./StepProduct_Modal_ProductSelection.vue";
import { fetchProductData } from "@/data.js";
import { Utils } from "@/utils.js";

const router = useRouter();
const store = useMainStore();

// Store cleanup function for focus trap
let cleanupFocusTrap = null;

onMounted(() => {
	$log.log(`[StepProduct:onMounted] Mounted the product step`);
	$log.log(
		`[StepProduct:onMounted] store.user.currentStepName: ${JSON.stringify(store.user.currentStepName, null, 4)}`,
	);

	window.addEventListener("keydown", handleKeydown);

	let productDataReadyInterval = null;
	const productDataReady = () => {
		if (store.products && store.products.length > 0) {
			$log.log(`[StepProduct:productDataReady] Product data loaded`);
			if (store.user?.currentStepName == "Product") {
				// $log.log(`${store.validation[store.user.currentStepName].valid}`);
				// if (store.validation[store.user.currentStepName].valid) {
				const currentProduct = store.currentProduct;
				if (
					currentProduct &&
					currentProduct.product &&
					currentProduct.product !== ""
				) {
					open_modal_ProductOverlay(currentProduct.product);
				}
			}
			if (store.overlays.productvalidation.open) {
				nextTick(() => {
					modal_focusFirstElement();
					cleanupFocusTrap = Utils.trapModalFocus(".lux-modal", "validation");
				});
			}
			clearInterval(productDataReadyInterval);
		}
	};

	// Start the interval to check if data is ready to calc.
	productDataReadyInterval = setInterval(productDataReady, 50);
});

// Clean up the event listener when component is unmounted
onUnmounted(() => {
	window.removeEventListener("keydown", handleKeydown);
	// Ensure any overlay is closed when we leve the route.
	store.overlays.product.open = false;
	store.overlays.productvalidation.open = false;
	// Make sure no-scroll class is alwsys removed from body
	document.body.classList.remove("no-scroll");
});

// Function to close modal when Esc key is pressed
const handleKeydown = (event) => {
	if (event.key === "Escape" && store.overlays.product.open) {
		cancel_modal_ProductOverlay();
	}
	if (event.key === "Escape" && store.overlays.productvalidation.open) {
		cancel_modal_ProductValidation();
	}
};

// // Save the selected product data to the user for easy access later.
// const handleSizeClick = (event) => {
//     store.user.productdata = store.overlays.product.data;
// };

const modal_focusFirstElement = () => {
	nextTick(() => {
		// Not using a DOM "ref" here as the "ref" attr is in the child component and the v-if and/or
		// transition seems to remove it from the DOM.
		const modal = document.querySelector(".lux-modal");
		if (modal) {
			const firstFocusableElement = modal.querySelector(
				'input, [tabindex]:not([tabindex="-1"])',
			);
			if (firstFocusableElement) {
				firstFocusableElement.focus();
			}
		}
	});
};

store.$subscribe((mutation, state) => {
	if (store.overlays.productvalidation.open) {
		open_modal_ProductValidation();
	}
});

const open_modal_ProductValidation = () => {
	if (!store.overlays.product.open) {
		document.body.classList.add("no-scroll");
		store.overlays.productvalidation.open = true;
		nextTick(() => {
			// Create a focus trap within the modal while it is open.
			modal_focusFirstElement();
			cleanupFocusTrap = Utils.trapModalFocus(".lux-modal", "validation");
		});
	} else {
		// TODO See comment in store.js
		// Ideally we would not need to handle this situation. The Store should not trigger the display of the modal
		// immediately. We should be picking up something here and using the logic above (only).
		store.overlays.productvalidation.open = false;
	}
};

const cancel_modal_ProductValidation = () => {
	document.body.classList.remove("no-scroll");
	store.overlays.productvalidation.open = false;
	// Clean up focus trap
	if (cleanupFocusTrap) {
		cleanupFocusTrap();
		cleanupFocusTrap = null;
	}
};

/*
    ------------------------------------------------------------------------
    Cancel/close the Product Selection Modal.

    Called when user clicks X button or presses Escape.
    Clears products from store if user hasn't completed selection.
    ------------------------------------------------------------------------
    */
const cancel_modal_ProductOverlay = () => {
	$log.log(`[StepProduct:cancel_modal_ProductOverlay] Closing product overlay`);
	store.overlays.product.open = false;
	store.overlays.product.data = "";
	// Clear products if user cancelled without completing selection
	store.clearProducts();
	// Note: scroll unlock is handled by BaseModalPanel
	store.validation.Product.showErrors = false;

	// Clean up focus trap
	if (cleanupFocusTrap) {
		cleanupFocusTrap();
		cleanupFocusTrap = null;
	}
};

/*
    ------------------------------------------------------------------------
    Open the Product Selection Modal Overlay.

    Called when user clicks on a product in the product grid.
    Loads the product data into the store and opens the modal.
    Product is added to store immediately so ProductSizeSelector can update it.
    ------------------------------------------------------------------------
    */
const open_modal_ProductOverlay = (productId) => {
	$log.log(`[StepProduct:open_modal_ProductOverlay] Opening product overlay for: ${productId}`);
	store.overlays.product.open = true;
	// Note: scroll lock is handled by BaseModalPanel with scrollbar compensation

	// Load main product data from catalog
	const productData = store.products.find(
		(product) => product.id === productId,
	);

	// Validate product data exists
	if (!productData) {
		$log.error(`[StepProduct:open_modal_ProductOverlay] Product not found in catalog: ${productId}`);
		store.overlays.product.open = false;
		return;
	}

	// Store product data in overlay state for modal to use
	store.overlays.product.data = productData;
	$log.log(`[StepProduct:open_modal_ProductOverlay] productData: ${JSON.stringify(productData, null, 4)}`);

	// Load extended product data (lumen specs, etc.)
	const extendedData =
		store.productsExtended && store.productsExtended[productId]
			? store.productsExtended[productId]
			: [];
	store.overlays.product.extendedData = extendedData;

	if (extendedData.length > 0) {
		$log.log(
			`[StepProduct:open_modal_ProductOverlay] Loaded extended data for product ${productId}: ${extendedData.length} variants`,
		);
	} else {
		$log.log(`[StepProduct:open_modal_ProductOverlay] No extended data found for product ${productId}`);
	}

	// Clear all existing products before adding new primary product
	// This ensures a fresh start when selecting a new product
	store.clearProducts();

	// Add new product as primary (with empty size - will be filled by ProductSizeSelector)
	store.addProduct({
		product: productId,
		size: "",
		size_variant: "",
		metric_sabins: "",
		data: productData,
		dataExtended: [],
		calculatedUnits: null,
		manualUnits: null,
	});

	store.validation.Product.showErrors = false;

	nextTick(() => {
		modal_focusFirstElement();
		cleanupFocusTrap = Utils.trapModalFocus(".lux-modal");
	});
};
</script>
<!--
PRODUCT STEP
-->
<template>
	<div id="step-product" class="lux-step-root">
		<h2 class="lux-page-title">Choose Your Luxxbox Product</h2>
		<p class="lux-copy-normal">
			Choose your preferred Luxxbox acoustic lighting product from our range of
			high-performance solutions. A pop-up will provide additional details about
			the selected product, including a link to the spec sheet and size options.
		</p>

		<div class="lux-mt w-full bg-white py-16">
			<ul
				role="list"
				class="grid grid-cols-2 gap-10 bg-white sm:grid-cols-3 lg:grid-cols-5"
			>
				<li
					v-for="item in store.products"
					:key="item.id"
					class="col-span-1 flex flex-col divide-y divide-gray-200 bg-white text-center"
				>
					<a
						href="#"
						@click.prevent="open_modal_ProductOverlay(item.id)"
						class="hover-effect relative flex flex-1 flex-col overflow-hidden p-5 text-center bg-gray-20110"
					>
						<img
							class="top-0 mx-auto -mt-2 w-36 min-w-[150px]"
							:src="Utils.resolveHeroImage(item.images.hero)"
							:alt="`Product image of: ${item.name}.`"
						/>
						<h3 class="mt-6 text-sm font-bold uppercase text-gray-900">
							{{ item.name }}
						</h3>
					</a>
				</li>
			</ul>
		</div>

		<span class="hidden">
			<input
				type="hidden"
				name="stepProductValid"
				:value="store.stepProductIsValid"
				class="hidden"
			/>
		</span>
		<!--
        PRODUCT VALIDATION MODALOVERLAY
        (WHEN USER HAS NOT SELECTED A PRODUCT)
        -->
		<transition name="stepfade">
			<Teleport to="body">
				<ModalValidation
					:fnCancelModal="cancel_modal_ProductValidation"
					:fnFocusFirstElement="modal_focusFirstElement"
					v-if="store.overlays.productvalidation.open"
				/>
			</Teleport>
		</transition>
		<!--
        PRODUCT SELECTION MODAL OVERLAY
        Component stays mounted, visibility controlled by :show prop for smooth transitions
        -->
		<Teleport to="body">
			<ModalSelection
				:show="store.overlays.product.open"
				:fnCancelModal="cancel_modal_ProductOverlay"
			/>
		</Teleport>
	</div>
</template>

<style scoped>
label.selected {
	@apply bg-black text-white;
}

.hover-effect {
	text-align: center;
	transition: transform 1s ease;
	/* Smooth transition on transform */
	transform-origin: center;
	/* Ensure scaling happens from the center */
}

.hover-effect:hover {
	transform: scale(1.05);
	/* Scale the element by 5% */
}
</style>
